import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
// import { useTranslation } from 'react-i18next';
import Button from './Button/Button';
import EllipsisVerticalIcon from './Icons/EllipsisVerticalIcon';
import CheckIcon from './Icons/CheckIcon';
import { theme } from '../theme';
import Typography from './Typography';

const FilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  flex-grow: 1;
`;

const Tabs = styled.div`
  align-items: flex-start;
  background: ${({ theme }) => theme.fill.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xl};
  display: flex;
  padding: ${({ theme }) => theme.sizing.xxs};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-grow: 1;
  }
`;

const ButtonTab = styled(Button)`
  background-color: transparent;
  border-radius: ${({ theme }) => theme.radius.xl};
  color: ${({ theme }) => theme.colors.defaultWeak};
  transition: all 0.3s ease-in-out;
  padding-left: ${({ theme }) => theme.sizing.sm};
  padding-right: ${({ theme }) => theme.sizing.sm};


  &.Active {
    background-color: ${({ theme }) => theme.fill.defaultMain};
    box-shadow: 0 0 4px rgb(0 0 0 / 8%), 0 4px 12px rgb(0 0 0 / 4%);
    color: ${({ theme }) => theme.colors.defaultMain};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 14px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-grow: 1;
  }
`;


const DropdownContainer = styled.div`
  display: inline-block;
  position: relative;
`;

const DropdownButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  // padding: 12px;
  background: ${({ theme }) => theme.fill.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xl};

  &:hover {
    background: ${({ theme }) => theme.fill.defaultStrong};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 36px;
    height: 36px;
  }
`;

const DropdownMenu = styled.div`
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  box-shadow: 0 2px 8px rgb(0 0 0 / 15%);
  display: flex;
  flex-direction: column;
  margin: 4px 0 0;
  min-width: 200px;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 1100;
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.sizing.xs};
`;

const DropdownMenuItem = styled.div`
  align-items: center;
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.xs};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

const MenuItemContent = styled(Typography)`
  flex: 1;
`;

const CheckIconWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 16px;
  height: 16px;
  opacity: ${({ $isSelected }) => $isSelected ? 1 : 0};
`;

const EventsTimeFilter = ({
  activeTab,
  onTabChange,
  showCancelledEvents,
  onCancelledEventsChange,
  upcomingLabel = "Próximos",
  pastLabel = "Pasados",
  includeCancelledLabel,
  excludeCancelledLabel
}) => {
  // const { t } = useTranslation('hostedEvents');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const cancelledOptions = [
    { label: excludeCancelledLabel, value: 'exclude' },
    { label: includeCancelledLabel, value: 'include' }
  ];

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  };

  const handleOptionClick = (optionValue) => {
    onCancelledEventsChange([optionValue]);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <FilterContainer>
      <Tabs>
        <ButtonTab
          $variant="defaultInverse"
          size="m"
          sizeTablet="m"
          sizeMobile="s"
          className={activeTab === 'upcoming' ? 'Active' : ''}
          onClick={() => onTabChange('upcoming')}
        >
          {upcomingLabel}
        </ButtonTab>
        <ButtonTab
          $variant="defaultInverse"
          size="m"
          sizeTablet="m"
          sizeMobile="s"
          className={activeTab === 'past' ? 'Active' : ''}
          onClick={() => onTabChange('past')}
        >
          {pastLabel}
        </ButtonTab>
      </Tabs>

      <DropdownContainer ref={dropdownRef}>
        <DropdownButton
          $variant="ghost"
          size="l"
          sizeTablet="l"
          sizeMobile="m"
          onClick={toggleDropdown}
        >
          <EllipsisVerticalIcon
            fill={theme.colors.defaultStrong}
            width={20}
            height={20}
          />
        </DropdownButton>

        {isDropdownOpen && (
          <DropdownMenu>
            <InnerWrapper>
              {cancelledOptions.map((option) => {
                const isSelected = showCancelledEvents.includes(option.value);
                return (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => handleOptionClick(option.value)}
                  >
                    <MenuItemContent $variant="body-2-medium">
                      {option.label}
                    </MenuItemContent>
                    <CheckIconWrapper $isSelected={isSelected}>
                      <CheckIcon
                        fill={theme.colors.brandMain}
                        width={16}
                        height={16}
                      />
                    </CheckIconWrapper>
                  </DropdownMenuItem>
                );
              })}
            </InnerWrapper>
          </DropdownMenu>
        )}
      </DropdownContainer>
    </FilterContainer>
  );
};

EventsTimeFilter.propTypes = {
  activeTab: PropTypes.oneOf(['upcoming', 'past']).isRequired,
  onTabChange: PropTypes.func.isRequired,
  showCancelledEvents: PropTypes.array.isRequired,
  onCancelledEventsChange: PropTypes.func.isRequired,
  upcomingLabel: PropTypes.string,
  pastLabel: PropTypes.string,
  includeCancelledLabel: PropTypes.string,
  excludeCancelledLabel: PropTypes.string,
};

export default EventsTimeFilter;